import { ReactElement, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { CustomTooltip, CustomTooltipPlacement } from '../PIM/common/CustomTooltip';
import './TruncatedText.scss';

export interface TruncatedTextProps {
  text: string;
  lines?: number;
  className?: string;
  placement?: CustomTooltipPlacement;
  tooltipClass?: string;
  testId?: string;
}

const DEFAULT_LINES = 1;
const DEFAULT_TOOLTIP_CLASS = 'text-tooltip';

/**
 * Clamps `text` to `lines` and shows the full value in a tooltip only when it overflows.
 * @param {TruncatedTextProps} props - Text, clamp height, tooltip placement.
 * @returns {ReactElement} The clamped text, wrapped in a tooltip when clipped.
 */
export function TruncatedText({
  text,
  lines = DEFAULT_LINES,
  className,
  placement = 'top',
  tooltipClass = DEFAULT_TOOLTIP_CLASS,
  testId,
}: TruncatedTextProps): ReactElement {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isClipped, setIsClipped] = useState(false);

  const measure = useCallback((): void => {
    const node = textRef.current;
    if (!node) {
      return;
    }
    setIsClipped(node.scrollHeight > node.clientHeight + 1 || node.scrollWidth > node.clientWidth + 1 || isTextWiderThanBox(node));
  }, []);

  useLayoutEffect(measure, [measure, text, lines]);

  useEffect(() => {
    const node = textRef.current;
    if (!node || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [measure]);

  const content = (
    <span
      ref={textRef}
      className={['truncated-text', className].filter(Boolean).join(' ')}
      style={{ WebkitLineClamp: lines }}
      data-testid={testId}
    >
      {text}
    </span>
  );

  if (!isClipped) {
    return content;
  }

  return (
    <CustomTooltip tooltipText={text} placement={placement} tooltipClass={tooltipClass}>
      {content}
    </CustomTooltip>
  );
}

export default TruncatedText;

/**
 * Sub-pixel overflow check for single-line text. `scrollWidth`/`clientWidth` are rounded to whole
 * pixels, so text a fraction of a pixel too wide reads as fitting while the browser still draws the
 * ellipsis (a 39.6px "Image" header in a 39.4px box). A Range measures the unclipped text exactly.
 * @param node - the truncating span.
 * @returns true when the text's real width exceeds the span's box; false where Range layout is unavailable.
 */
function isTextWiderThanBox(node: HTMLElement): boolean {
  if (typeof document.createRange !== 'function') return false;
  const range = document.createRange();
  range.selectNodeContents(node);
  if (typeof range.getBoundingClientRect !== 'function') return false;
  return range.getBoundingClientRect().width > node.getBoundingClientRect().width;
}
